import axios from "axios"
import { supabase } from "../supabaseClient"

const api = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL ? `${process.env.REACT_APP_SERVER_URL}/api` : "/api",
  headers: {
    "Content-Type": "application/json",
  },
})

api.interceptors.request.use(
  async (config) => {
    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (session) {
      config.headers["user-id"] = session.user.id
    }

    try {
      const authContextStr = localStorage.getItem("authContext")
      if (authContextStr) {
        const authContext = JSON.parse(authContextStr)
        if (authContext.isAdmin && authContext.currentClientId && authContext.clients) {
          const selectedClient = authContext.clients.find((c: any) => c.id === authContext.currentClientId)
          if (selectedClient) {
            config.headers["x-admin-target-company"] = selectedClient.company_name
          }
        }
      }
    } catch (e) {
      // Silently fail if localStorage access fails
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

export interface CallCost {
  product_costs: Array<{
    product_name: string
    unit_price: number
    cost: number
  }>
  total_duration_seconds: number
  total_duration_unit_price: number
  total_one_time_price: number
  combined_cost: number
}

// Update the Call interface to include public_log_url and duration_ms
export interface Call {
  call_id: string
  call_type: "web_call" | "phone_call"
  agent_id: string
  call_status: "registered" | "ongoing" | "ended" | "error"
  start_timestamp: number | string
  end_timestamp: number | string
  disconnection_reason: string
  call_cost: CallCost
  transcript?: string
  recording_url?: string
  public_log_url?: string
  duration_ms?: number
  call_analysis?: {
    sentiment: "Positive" | "Negative" | "Neutral"
    call_successful?: boolean
  }
  from_number?: string
  to_number?: string
  duration_seconds?: number
}

export interface ListCallsResponse {
  calls: Call[]
}

export interface CreateWebCallResponse {
  call_type: "web_call"
  access_token: string
  call_id: string
  agent_id: string
  call_status: "registered" | "ongoing" | "ended" | "error"
  // ... (other fields)
}

// Add this interface for the createPhoneCall response
export interface CreatePhoneCallResponse {
  call_type: "phone_call"
  call_id: string
  agent_id: string
  call_status: "registered" | "ongoing" | "ended" | "error"
  from_number: string
  to_number: string
}

class CallService {
  async listCalls(options: any = {}): Promise<Call[]> {
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      }

      const response = await api.post("/calls/list", options, { headers })

      return response.data
    } catch (error) {
      console.error("Error listing calls:", error)
      return []
    }
  }

  calculateCallCost(call: Call): number {
    // Calculate the cost of a single call
    // Base rate: $0.03 per minute (3 cents)
    const durationMinutes = this.calculateDurationMinutes(call)
    return Math.ceil(durationMinutes * 3) // Cost in cents
  }

  calculateDurationMinutes(call: Call): number {
    // Calculate duration in minutes
    if (call.duration_seconds) {
      return call.duration_seconds / 60
    }

    // If duration_seconds is not available, calculate from timestamps
    const startTime = new Date(call.start_timestamp).getTime()
    const endTime = new Date(call.end_timestamp).getTime()
    const durationMs = endTime - startTime
    return durationMs / (1000 * 60) // Convert ms to minutes
  }

  calculateTotalCost(calls: Call[]): number {
    // Calculate the total cost of all calls
    return calls.reduce((total, call) => total + this.calculateCallCost(call), 0)
  }

  createWebCall = async (agentId: string, options?: any) => {
    console.log("Call service createWebCall with options:", options)
    const requestData = {
      agent_id: agentId,
      ...options,
    }
    console.log("Request data being sent to API:", requestData)
    const response = await api.post<CreateWebCallResponse>("/calls/web", requestData)
    return response.data
  }

  /**
   * Outbound PSTN via AI Agents Backend (supports Retell LLM dynamic_vars_values).
   */
  createPhoneCall = async (agentId: string, fromNumber: string, toNumber: string, options?: any) => {
    console.log("Call service createPhoneCall with options:", options)
    const { dynamic_vars_values, ...rest } = options || {}
    const requestData: Record<string, unknown> = {
      agent_id: agentId,
      from_number: fromNumber,
      to_number: toNumber,
      ...rest,
    }
    if (
      dynamic_vars_values &&
      typeof dynamic_vars_values === "object" &&
      !Array.isArray(dynamic_vars_values)
    ) {
      requestData.dynamic_vars_values = dynamic_vars_values
    }
    console.log("Request data being sent to API:", requestData)
    const response = await api.post<CreatePhoneCallResponse>(
      "/calls/make-outbound-voice-call",
      requestData,
    )
    return response.data
  }
}

export const callService = new CallService()
